import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../api/rpc';
import { queryKeys } from '../../../api/queryKeys';
import type { BritProtocol } from '../../../api/types';

/**
 * Fetch full hanging protocols for the given scope.
 *
 * - `'user'`  — current user's protocols only
 * - `'site'`  — site-level defaults only
 * - `'all'`   — both user + site (default)
 *
 * Protocols change infrequently, so a 5-minute staleTime keeps
 * traffic low while ensuring fresh data after saves/deletes.
 */
export function useProtocols(scope: 'user' | 'site' | 'all' = 'all') {
  return useQuery({
    queryKey: queryKeys.protocols.all(scope),
    queryFn: () => api.getProtocolsFull({ scope }),
    staleTime: 5 * 60 * 1000,
  });
}

/** Save a full protocol and invalidate the cache. */
export function useSaveProtocol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ protocol, scope }: { protocol: BritProtocol; scope?: 'user' | 'site' }) =>
      api.saveProtocolFull(protocol, scope),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['protocols'] });
    },
  });
}

/** Delete a protocol and invalidate the cache. */
export function useDeleteProtocol() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, scope }: { id: string; scope?: 'user' | 'site' }) =>
      scope ? api.deleteProtocolScoped(id, scope) : api.deleteProtocol(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['protocols'] });
    },
  });
}
